import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useParams } from 'react-router-dom';
import Footer from '../../components/Footer';
import LandingHeader from '../components/LandingHeader';
import { appStoreUrl, getBlogPost } from '../content/blogPosts';
import { isIOSDevice } from '../utils/deviceDetection';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = getBlogPost(slug);
  const isIOS = isIOSDevice();

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="public-scroll-page bg-white">
      <Helmet>
        <title>{post.title} | DinnerBrain Blog</title>
        <meta name="description" content={post.description} />
      </Helmet>
      <LandingHeader />
      <main style={{ paddingTop: isIOS ? 'calc(5rem + env(safe-area-inset-top, 0px))' : '5rem' }}>
        <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <Link to="/blog" className="mb-8 inline-flex min-h-[44px] items-center text-sm font-semibold text-[#4A6651] hover:underline">
            ← Back to blog
          </Link>

          <header className="mb-10">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#4A6651]">
              {post.readTime} · {new Date(post.date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <h1 className="mb-5 text-4xl font-bold leading-tight text-slate-900 sm:text-5xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {post.title}
            </h1>
            <p className="text-xl text-slate-600">{post.description}</p>
          </header>

          <div className="space-y-10 text-lg leading-8 text-slate-700">
            {post.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="mb-4 text-2xl font-semibold text-slate-900">{section.heading}</h2>
                {section.paragraphs?.map((paragraph) => (
                  <p key={paragraph} className="mb-4">{paragraph}</p>
                ))}
                {section.bullets && (
                  <ul className="mt-4 space-y-3 rounded-2xl bg-[#F4F8F6] p-6">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-3">
                        <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-[#4A6651]" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <aside className="mt-12 rounded-3xl bg-[#E6EFEA] p-6 sm:p-8">
            <h2 className="mb-3 text-2xl font-semibold text-slate-900">Make dinner easier to decide.</h2>
            <p className="mb-6 text-slate-700">
              Start a DinnerBrain trial to keep your favorite recipes, meal plans, and shopping lists together so weeknight dinner has fewer loose ends.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-[#4A6651] px-5 py-2 font-semibold text-white hover:bg-[#3d5444]" to="/onboarding">
                Start your free trial
              </Link>
              <a className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-[#4A6651] px-5 py-2 font-semibold text-[#4A6651] hover:bg-white" href={appStoreUrl}>
                Get the iPhone app
              </a>
            </div>
          </aside>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;
